/* eslint-disable max-statements */
import React from 'react';
import { switchToBSC } from '@profile/src/utils/ethereum';
import { EipError } from '@profile/src/redux/types';
import WarningWrongNetwork from '../Components/Warning/WarningWrongNetwork';
import { Dialog } from '@profile/src/Components/DialogModal_V2';
import { handleWalletError } from '@profile/src/ConnectWallet_V2/config';
import WarningUserBanned from '../Components/Warning/WarningUserBanned';
import ConnectWalletModal from '../Components/ConnectWalletModal';
import { useTypedSelector } from '@profile/src/redux/utils';
import { UserStatus } from '@profile/src/utils/types';
import WarningWrongAddress from '../Components/Warning/WarningWrongAddress';
import WarningWrongAddressModal from '../Components/WarningWrongAddressModal';
import styles from './styles.module.scss';
import { navigateToConnectWallet } from '@profile/src/utils/navigate';
export const UserWalletContext = /*#__PURE__*/React.createContext({
  checkWalletError: () => false
});

const UserWalletProvider = ({
  children,
  showWarning
}) => {
  const dialogRef = React.useRef(null);
  const userAddress = useTypedSelector(state => {
    var _state$userProfile$us;

    return (_state$userProfile$us = state.userProfile.user) === null || _state$userProfile$us === void 0 ? void 0 : _state$userProfile$us.address;
  });
  const signedIn = useTypedSelector(state => !!state.userSignIn.accessToken);
  const accountWallet = useTypedSelector(state => {
    var _state$connectWallet$;

    return (_state$connectWallet$ = state.connectWallet.accounts) === null || _state$connectWallet$ === void 0 ? void 0 : _state$connectWallet$[0];
  });
  const userStatus = useTypedSelector(state => {
    var _state$userProfile$us2;

    return (_state$userProfile$us2 = state.userProfile.user) === null || _state$userProfile$us2 === void 0 ? void 0 : _state$userProfile$us2.status;
  });
  const isUserBanned = userStatus === UserStatus.Banned;
  const connectWalletError = useTypedSelector(state => state.connectWallet.error);
  const isWrongAddress = userAddress && accountWallet ? accountWallet.toLowerCase() !== userAddress.toLowerCase() : false;
  const [isWarningWrongNetwork, setWarningWrongNetwork] = React.useState(false);
  const [isWarningWrongAddress, setWarningWrongAddress] = React.useState(false);
  const [isWarningUserBanned, setWarningUserBanned] = React.useState(isUserBanned);
  const [walletError, setWalletError] = React.useState(EipError.NO_ERROR);
  const [isShowWarningAddressModal, setShowWarningAddressModal] = React.useState(false);
  const [isShowConnectWalletModal, setShowConnectWalletModal] = React.useState(false);
  const checkWalletError = React.useCallback(() => {
    if (!dialogRef.current) {
      return false;
    }

    if (isWrongAddress) {
      setShowWarningAddressModal(true);
      return true;
    }

    if (!signedIn) {
      setShowConnectWalletModal(true);
      return true;
    }

    if (walletError) {
      handleWalletError(walletError, {
        showDialog: dialogRef.current.showDialog,
        hideDialog: dialogRef.current.hideDialog,
        handleSwitchToBSC: switchToBSC
      });
      return true;
    }

    return false;
  }, [isWrongAddress, signedIn, walletError]);
  const context = React.useMemo(() => {
    return {
      walletError,
      checkWalletError,
      isWrongAddress,
      isUserBanned
    };
  }, [checkWalletError, isUserBanned, isWrongAddress, walletError]);
  React.useEffect(() => {
    if (connectWalletError) {
      setWalletError(connectWalletError);
      setWarningWrongNetwork(connectWalletError === EipError.METAMASK_WRONG_CHAIN);
    } else {
      setWarningWrongNetwork(false);
      setWalletError(EipError.NO_ERROR);
    }
  }, [connectWalletError]);
  React.useEffect(() => {
    if (isWrongAddress) {
      setWarningWrongAddress(true);
    } else {
      setWarningWrongAddress(false);
    }
  }, [isWrongAddress]);
  React.useEffect(() => {
    if (userStatus === UserStatus.Banned) {
      setWarningUserBanned(true);
    } else {
      setWarningUserBanned(false);
    }
  }, [userStatus]);
  const firstWarningName = React.useMemo(() => {
    if (isWarningWrongNetwork) {
      return 'wrongNetwork';
    }

    if (isWarningWrongAddress) {
      return 'wrongAddress';
    }

    if (isWarningUserBanned) {
      return 'userBanned';
    }

    return undefined;
  }, [isWarningUserBanned, isWarningWrongAddress, isWarningWrongNetwork]);

  const renderModal = () => {
    if (isShowConnectWalletModal) {
      return /*#__PURE__*/React.createElement(ConnectWalletModal, {
        onClose: () => {
          setShowConnectWalletModal(false);
        },
        onConnectButtonPressed: () => {
          setShowConnectWalletModal(false);
          navigateToConnectWallet({
            backTo: window.location.pathname,
            redirectTo: window.location.pathname
          });
        }
      });
    }

    if (isShowWarningAddressModal) {
      return /*#__PURE__*/React.createElement(WarningWrongAddressModal, {
        onClose: () => {
          setShowWarningAddressModal(false);
        }
      });
    }

    return undefined;
  };

  return /*#__PURE__*/React.createElement(UserWalletContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.root
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.banner
  }, showWarning && firstWarningName === 'wrongNetwork' && /*#__PURE__*/React.createElement(WarningWrongNetwork, {
    onClose: () => {
      setWarningWrongNetwork(false);
    }
  }), showWarning && firstWarningName === 'userBanned' && /*#__PURE__*/React.createElement(WarningUserBanned, {
    onClose: () => {
      setWarningUserBanned(false);
    }
  }), showWarning && firstWarningName === 'wrongAddress' && /*#__PURE__*/React.createElement(WarningWrongAddress, {
    onClose: () => {
      setWarningWrongAddress(false);
    }
  })), children, /*#__PURE__*/React.createElement(Dialog, {
    ref: dialogRef
  }), renderModal()));
};

export const useUserWalletContext = () => {
  const {
    walletError,
    checkWalletError,
    isWrongAddress,
    isUserBanned
  } = React.useContext(UserWalletContext);
  return {
    walletError,
    checkWalletError,
    isWrongAddress,
    isUserBanned
  };
};
export default UserWalletProvider;